import { computed, reactive } from "vue"
import type { Unsubscribe } from "../services/apiClient"
import { householdService } from "../services/householdService"
import { type InvitationResult, invitationService } from "../services/invitationService"
import {
  type ActiveBudgetContext,
  BudgetContext,
  type Household,
  type HouseholdInvitation,
  HouseholdRole,
  ROLE_PERMISSIONS,
} from "../types/household"
import { logger } from "../utils/logger"
import { scheduleSave } from "../utils/storageQuota"

function persistActiveContext(): void {
  scheduleSave("activeBudgetContext", () => JSON.stringify(state.activeContext))
}

interface HouseholdState {
  households: Household[]
  activeContext: ActiveBudgetContext
  pendingInvitations: HouseholdInvitation[]
  // Invitations the selected household has sent (inviter/admin view)
  sentInvitations: HouseholdInvitation[]
  isLoading: boolean
  error: string | null
}

const state = reactive<HouseholdState>({
  households: [],
  activeContext: {
    context: BudgetContext.PERSONAL,
  },
  pendingInvitations: [],
  sentInvitations: [],
  isLoading: false,
  error: null,
})

// Unsubscribe functions for real-time listeners
let unsubscribeHouseholds: Unsubscribe | null = null
let unsubscribeInvitations: Unsubscribe | null = null
let unsubscribeSentInvitations: Unsubscribe | null = null

// Computed properties
export const currentHousehold = computed(() => {
  if (state.activeContext.context === BudgetContext.HOUSEHOLD && state.activeContext.householdId) {
    return state.households.find((h) => h.id === state.activeContext.householdId) || null
  }
  return null
})

export const currentUserRole = computed(() => state.activeContext.userRole)

export const currentPermissions = computed(() => {
  if (state.activeContext.userRole) {
    return ROLE_PERMISSIONS[state.activeContext.userRole]
  }
  return null
})

export const isPersonalContext = computed(() => state.activeContext.context === BudgetContext.PERSONAL)

export const isHouseholdContext = computed(() => state.activeContext.context === BudgetContext.HOUSEHOLD)

// The active household id when in household context, else null. Used to scope
// API requests (sent as the X-Household-Id header).
export const activeHouseholdId = computed(() =>
  state.activeContext.context === BudgetContext.HOUSEHOLD ? (state.activeContext.householdId ?? null) : null,
)

// Actions

/**
 * Initialize household data for a user
 */
export async function initializeHouseholds(userId: string, userEmail: string): Promise<void> {
  // Re-init (e.g. re-login within the same session) must release the previous
  // Realtime channels before overwriting the handles — otherwise they leak.
  if (unsubscribeHouseholds) {
    unsubscribeHouseholds()
    unsubscribeHouseholds = null
  }
  if (unsubscribeInvitations) {
    unsubscribeInvitations()
    unsubscribeInvitations = null
  }

  state.isLoading = true
  state.error = null

  try {
    // Subscribe to user's households
    unsubscribeHouseholds = householdService.subscribeToUserHouseholds(userId, (households) => {
      state.households = households
    })

    // Subscribe to user's invitations
    unsubscribeInvitations = householdService.subscribeToUserInvitations(userEmail, (invitations) => {
      state.pendingInvitations = invitations
    })

    // Load initial data
    const [households, invitations] = await Promise.all([
      householdService.getUserHouseholds(userId),
      householdService.getUserInvitations(userEmail),
    ])

    state.households = households
    state.pendingInvitations = invitations
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    state.error = message
    throw error
  } finally {
    state.isLoading = false
  }
}

/**
 * Cleanup subscriptions
 */
export function cleanupHouseholds(): void {
  if (unsubscribeHouseholds) {
    unsubscribeHouseholds()
    unsubscribeHouseholds = null
  }
  if (unsubscribeInvitations) {
    unsubscribeInvitations()
    unsubscribeInvitations = null
  }
  clearHouseholdInvitations()

  state.households = []
  state.pendingInvitations = []
  state.activeContext = {
    context: BudgetContext.PERSONAL,
  }
}

/**
 * Subscribe to the invitations the given household has sent (admin/inviter
 * view). Replaces any existing subscription.
 */
export function subscribeToHouseholdInvitations(householdId: string): void {
  clearHouseholdInvitations()
  unsubscribeSentInvitations = householdService.subscribeToHouseholdInvitations(householdId, (invitations) => {
    state.sentInvitations = invitations
  })
}

/**
 * Stop watching sent invitations and clear them.
 */
export function clearHouseholdInvitations(): void {
  if (unsubscribeSentInvitations) {
    unsubscribeSentInvitations()
    unsubscribeSentInvitations = null
  }
  state.sentInvitations = []
}

/**
 * Revoke (delete) a pending invitation. Only the original inviter is permitted
 * by security rules.
 */
export async function revokeInvitation(invitationId: string): Promise<void> {
  state.isLoading = true
  state.error = null
  try {
    await householdService.deleteInvitation(invitationId)
  } catch (error) {
    state.error = error instanceof Error ? error.message : String(error)
    throw error
  } finally {
    state.isLoading = false
  }
}

/**
 * Create a new household
 */
export async function createHousehold(
  userId: string,
  name: string,
  userEmail: string,
  userDisplayName: string | null,
  userPhotoURL: string | null,
  description?: string,
): Promise<Household> {
  state.isLoading = true
  state.error = null

  try {
    const household = await householdService.createHousehold(
      userId,
      name,
      userEmail,
      userDisplayName,
      userPhotoURL,
      description,
    )

    // Seed the list immediately instead of waiting for the Realtime refetch:
    // HouseholdView switches into the new household right after this returns,
    // and switchToHouseholdContext throws "Household not found" if the
    // refetch hasn't landed yet — surfacing a false "create failed" toast
    // for a household that was created fine. The refetch reconciles later.
    if (!state.households.some((h) => h.id === household.id)) {
      state.households.push(household)
    }
    return household
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    state.error = message
    throw error
  } finally {
    state.isLoading = false
  }
}

/**
 * Update household details
 */
export async function updateHousehold(
  householdId: string,
  // See householdService.updateHousehold: null clears the description (and
  // the household photo).
  updates: Partial<Pick<Household, "name" | "settings">> & {
    description?: string | null
    imageId?: string | null
  },
): Promise<void> {
  state.isLoading = true
  state.error = null

  try {
    await householdService.updateHousehold(householdId, updates)
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    state.error = message
    throw error
  } finally {
    state.isLoading = false
  }
}

/**
 * Delete a household
 */
export async function deleteHousehold(householdId: string): Promise<void> {
  state.isLoading = true
  state.error = null

  try {
    await householdService.deleteHousehold(householdId)

    // Switch to personal context if we deleted the active household
    if (state.activeContext.householdId === householdId) {
      await switchToPersonalContext()
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    state.error = message
    throw error
  } finally {
    state.isLoading = false
  }
}

/**
 * Invite a user to household
 * Uses callable Cloud Function to create invitation and send email in one transaction
 */
export async function inviteToHousehold(
  householdId: string,
  invitedEmail: string,
  _invitedBy: string, // Kept for API compatibility, but auth is handled server-side
  invitedByName: string,
  role: HouseholdRole = HouseholdRole.FAMILY_MEMBER,
): Promise<InvitationResult> {
  state.isLoading = true
  state.error = null

  try {
    const household = state.households.find((h) => h.id === householdId)
    if (!household) {
      throw new Error("Household not found")
    }

    const result = await invitationService.sendInvitation(
      householdId,
      household.name,
      invitedEmail,
      invitedByName,
      role,
    )

    if (!result.success) {
      throw new Error(result.error || "Failed to send invitation")
    }

    return result
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    state.error = message
    throw error
  } finally {
    state.isLoading = false
  }
}

/**
 * Accept an invitation
 */
export async function acceptInvitation(
  invitationId: string,
  userId: string,
  userEmail: string,
  userDisplayName: string | null,
  userPhotoURL: string | null,
): Promise<void> {
  state.isLoading = true
  state.error = null

  try {
    await householdService.acceptInvitation(invitationId, userId, userEmail, userDisplayName, userPhotoURL)

    // Invitation will be removed via real-time listener
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    state.error = message
    throw error
  } finally {
    state.isLoading = false
  }
}

/**
 * Decline an invitation
 */
export async function declineInvitation(invitationId: string): Promise<void> {
  state.isLoading = true
  state.error = null

  try {
    await householdService.declineInvitation(invitationId)
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    state.error = message
    throw error
  } finally {
    state.isLoading = false
  }
}

/**
 * Remove a member from household
 */
export async function removeMember(householdId: string, userId: string): Promise<void> {
  state.isLoading = true
  state.error = null

  try {
    await householdService.removeMember(householdId, userId)
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    state.error = message
    throw error
  } finally {
    state.isLoading = false
  }
}

/**
 * Update a member's role
 */
export async function updateMemberRole(householdId: string, userId: string, newRole: HouseholdRole): Promise<void> {
  state.isLoading = true
  state.error = null

  try {
    await householdService.updateMemberRole(householdId, userId, newRole)
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    state.error = message
    throw error
  } finally {
    state.isLoading = false
  }
}

/**
 * Leave a household (remove yourself as a member)
 */
export async function leaveHousehold(householdId: string, userId: string): Promise<void> {
  await removeMember(householdId, userId)

  // Switch to personal context if we left the active household
  if (state.activeContext.householdId === householdId) {
    await switchToPersonalContext()
  }
}

/**
 * Switch to personal budget context. Resolves once the database context has
 * been updated; failures are surfaced via `state.error` (never rejects).
 */
export function switchToPersonalContext(): Promise<void> {
  state.activeContext = {
    context: BudgetContext.PERSONAL,
  }

  // Best-effort coalesced persistence — quota errors are logged inside
  // scheduleSave; we don't fail the context switch if storage is full.
  persistActiveContext()

  // Update database service context
  return updateDatabaseContext()
}

/**
 * Switch to household budget context. Validation errors ("Household not
 * found", "not a member") throw synchronously; the returned promise resolves
 * once the database context has been updated, with failures surfaced via
 * `state.error` (never rejects).
 */
export function switchToHouseholdContext(householdId: string, userId: string): Promise<void> {
  const household = state.households.find((h) => h.id === householdId)
  if (!household) {
    throw new Error("Household not found")
  }

  const member = household.members.find((m) => m.userId === userId)
  if (!member) {
    throw new Error("You are not a member of this household")
  }

  state.activeContext = {
    context: BudgetContext.HOUSEHOLD,
    householdId: household.id,
    householdName: household.name,
    userRole: member.role,
  }

  // Best-effort coalesced persistence — quota errors are logged inside
  // scheduleSave; we don't fail the context switch if storage is full.
  persistActiveContext()

  // Update database service context
  return updateDatabaseContext()
}

/**
 * Update database service context based on active context
 */
async function updateDatabaseContext(): Promise<void> {
  try {
    const { databaseService } = await import("../services/databaseService")
    const { cleanupFirestore, initializeFirestore } = await import("./firestoreStore")

    if (state.activeContext.context === BudgetContext.HOUSEHOLD && state.activeContext.householdId) {
      // Switch to household context
      databaseService.setHouseholdContext(state.activeContext.householdId)
    } else {
      // Switch to personal context
      databaseService.setPersonalContext()
    }

    // Cached AI insights were computed from the previous context's data —
    // drop them so personal insights never show up in household view (or
    // vice versa).
    const { aiInsights } = await import("./aiInsights")
    aiInsights.clearCache()

    // Same for the assistant chat: its conversation belongs to the previous
    // context, and reusing its conversationId with the new X-Household-Id
    // would write across contexts.
    const { resetChat } = await import("./aiChatStore")
    resetChat()

    // And the grocery store: it holds its own subscriptions, so an open
    // GroceryView would keep showing the old context's list while writes go
    // to the new one.
    const { rescopeGroceryStore } = await import("./groceryStore")
    rescopeGroceryStore()

    // Reinitialize firestore to reload data with new context
    cleanupFirestore()
    const { currentUser } = await import("./authStore")
    if (currentUser.value) {
      await initializeFirestore(currentUser.value)
    }
  } catch (error) {
    // Surface the failure on the store so the UI can react (the data layer is
    // now out of sync with the selected context), and keep the log for
    // diagnostics. Intentionally does not rethrow: fire-and-forget callers in
    // views would otherwise produce unhandled rejections.
    state.error = error instanceof Error ? error.message : String(error)
    logger.error("Failed to update database context", error)
    // state.error alone is invisible unless a view happens to render it — a
    // failed switch (e.g. offline) leaves the app showing the wrong context's
    // data, so tell the user directly.
    try {
      const { useToast } = await import("../composables/useToast")
      const { i18n } = await import("../i18n")
      useToast().error(i18n.global.t("household.context_switch_failed"))
    } catch {
      // Toast is best-effort; the log above already captured the failure.
    }
  }
}

/**
 * Restore context from localStorage. Resolves once the database context has
 * been updated to match the restored state.
 *
 * The stored blob is not user-scoped, so another account on the same device
 * may have written it. Called after `initializeHouseholds` has loaded the
 * current user's households, it validates the restored household against them
 * (mirroring `switchToHouseholdContext`) and derives the role from the loaded
 * membership rather than trusting the blob; anything that doesn't check out
 * falls back to personal context.
 */
export async function restoreActiveContext(): Promise<void> {
  const saved = localStorage.getItem("activeBudgetContext")
  if (!saved) {
    // No saved context, ensure we're in personal mode
    return updateDatabaseContext()
  }

  let restored: Partial<ActiveBudgetContext>
  try {
    restored = JSON.parse(saved) as Partial<ActiveBudgetContext>
  } catch (_error) {
    // Invalid saved data, reset to personal
    return switchToPersonalContext()
  }

  if (restored?.context === BudgetContext.HOUSEHOLD && restored.householdId) {
    const household = state.households.find((h) => h.id === restored.householdId)
    const { currentUser } = await import("./authStore")
    const member = household?.members.find((m) => m.userId === currentUser.value?.uid)
    if (!household || !member) {
      // The saved household isn't one of this user's households (stale blob,
      // different account, membership revoked) — don't apply it.
      return switchToPersonalContext()
    }
    state.activeContext = {
      context: BudgetContext.HOUSEHOLD,
      householdId: household.id,
      householdName: household.name,
      userRole: member.role,
    }
    persistActiveContext()
    return updateDatabaseContext()
  }

  state.activeContext = { context: BudgetContext.PERSONAL }
  // Update database context to match restored state
  return updateDatabaseContext()
}

/**
 * Get current user's role in a household
 */
export function getUserRoleInHousehold(householdId: string, userId: string): HouseholdRole | null {
  const household = state.households.find((h) => h.id === householdId)
  if (!household) return null

  const member = household.members.find((m) => m.userId === userId)
  return member?.role || null
}

/**
 * Check if user has a specific permission in current context
 */
export function hasPermission(permission: keyof (typeof ROLE_PERMISSIONS)[HouseholdRole]): boolean {
  if (state.activeContext.context === BudgetContext.PERSONAL) {
    // In personal context, user has all permissions
    return true
  }

  if (!state.activeContext.userRole) {
    return false
  }

  return ROLE_PERMISSIONS[state.activeContext.userRole][permission]
}

export function clearError(): void {
  state.error = null
}

// Export the reactive state
export const householdStore = {
  state,
  currentHousehold,
  currentUserRole,
  currentPermissions,
  isPersonalContext,
  isHouseholdContext,
}
